import './kitStyles';
import React, { useEffect, useRef } from 'react';
import { markKeyboardChange } from './inputMode';
import { isPhone } from './popRules';
import { layerBase, layerMode, layerRoot, EVERY_ROOT } from './layerScope';

/**
 * WEB-3310 юнит 3 — телефонный слой кита (эталон `design/cabinet-rescin/kit.jsx:615–649`,
 * кит 03:178–229; владелец 24.10: «когда клавиатура выезжает, их невозможно поднять»):
 *  - высота клавиатуры `--kb-h` = `innerHeight − visualViewport.height − offsetTop`
 *    (60 и меньше — `0px`, до целого) и видимая высота `--vvh` — на корне документа
 *    (их читают окна в верхнем слое и вынесенные шторки), по `resize`/`scroll` у
 *    `visualViewport` раз в кадр, без порогов и плавности; смена — отметка
 *    `markKeyboardChange()` (`ds/inputMode.ts`); нет `visualViewport` — ничего;
 *  - фокус ушёл с поля и другого поля, держащего клавиатуру, в фокусе нет — сразу
 *    `--kb-h: 0px`, `--vvh` = `innerHeight` (Safari сообщает о спрятанной клавиатуре
 *    через 2–3 с), перепроверка через 400 и 900 мс;
 *  - телефон: поле получило фокус — через 450 мс встаёт в видимую часть: внутри своей
 *    прокрутки (окно, шторка) — прокручивается только она, отступ 8; на странице —
 *    плавно, если поле ниже видимого низа − 16 (низ — верх прилипшей к низу панели,
 *    если она есть);
 *  - телефон: шторка из `TOP_SEL`, оставшаяся на месте (`position: fixed`, не в
 *    открытом окне, не вынесена в `.ds`, без своего `popover`) — в верхний слой
 *    браузера: `popover="manual"`, класс `w2a-top`, `showPopover()`; нет Popover API
 *    или вызов бросил — остаётся как была.
 * Ставится один раз рядом с `TipLayer`, `TouchRules`, `FogRules` (правило — `layerScope.ts`):
 * владельцем корня `.ds` внутри корня (витрина `/kit`) — наблюдатель и обработчики фокуса
 * на своём корне и только для узлов, чей ближайший `.ds` — он (вложенный корень судит
 * сам); либо оболочкой кабинета вне `.ds` с `everyRoot` — наблюдатель на `document.body`,
 * обработчики фокуса на `document`, судятся узлы с любым ближайшим `.ds`, узлы без `.ds`
 * (страницы на antd) — нет. Слушатели `visualViewport` — одни на страницу,
 * сколько бы корней ни стояло; всё снимается при размонтировании (последнего — и
 * переменные). Глобальных `window.__kbAt` нет (`ПЕРЕДАЧА-ФРОНТУ.md` §7).
 */
export const TOP_SEL =
  '.pop.menu, .pop[role=listbox], .an-pop, .hx-pop, .kdr-pop, .dft-pop, .ctx-pop, .dk-pop, .qm-pop, .sx-pop, .od-pop, .rtb-pop';
/** Держит клавиатуру (эталон `kit.jsx:640`). */
const KB_HOLD =
  'input:not([type=radio]):not([type=checkbox]):not([type=button]):not([type=submit]):not([type=range]):not([type=color]):not([type=file]), textarea, select, [contenteditable=true]';
/** Поле, которое подкручивается к видимой части (эталон `kit.jsx:641`). */
const TYPING = 'input, textarea, [contenteditable=true]';
const KB_MIN = 60;
const RECHECK_MS = [400, 900];
const SCROLL_MS = 450;
const GAP_BOX = 8;
const GAP_PAGE = 16;

/* ---- высота клавиатуры: одна на страницу ---- */
let users = 0;
let lk = -1;
let lh = -1;
let raf = 0;
let rechecks: ReturnType<typeof setTimeout>[] = [];

function put(): void {
  raf = 0;
  const vv = window.visualViewport;
  if (!vv) return;
  const kb0 = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
  const kb = kb0 > KB_MIN ? Math.round(kb0) : 0;
  const h = Math.round(vv.height);
  const r = document.documentElement.style;
  if (kb !== lk) {
    lk = kb;
    markKeyboardChange();
    r.setProperty('--kb-h', kb + 'px');
  }
  if (h !== lh) {
    lh = h;
    markKeyboardChange();
    r.setProperty('--vvh', h + 'px');
  }
}

function upd(): void {
  if (!raf) raf = requestAnimationFrame(put);
}

function startViewport(): void {
  users += 1;
  const vv = window.visualViewport;
  if (users > 1 || !vv) return;
  vv.addEventListener('resize', upd);
  vv.addEventListener('scroll', upd);
  upd();
}

function stopViewport(): void {
  users -= 1;
  const vv = window.visualViewport;
  if (users > 0 || !vv) return;
  vv.removeEventListener('resize', upd);
  vv.removeEventListener('scroll', upd);
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  rechecks.forEach(clearTimeout);
  rechecks = [];
  lk = -1;
  lh = -1;
  document.documentElement.style.removeProperty('--kb-h');
  document.documentElement.style.removeProperty('--vvh');
}

/** Клавиатуры уже нет: высота — сразу, потом перепроверка по `visualViewport`. */
function keyboardGone(): void {
  if (!window.visualViewport || users < 1) return;
  const a = document.activeElement;
  if (a && a.matches(KB_HOLD)) return;
  const r = document.documentElement.style;
  lk = 0;
  lh = window.innerHeight;
  markKeyboardChange();
  r.setProperty('--kb-h', '0px');
  r.setProperty('--vvh', lh + 'px');
  rechecks.forEach(clearTimeout);
  rechecks = RECHECK_MS.map((ms) => setTimeout(upd, ms));
}

/* ---- подкрутка поля ---- */
function scrollBox(t: Element): HTMLElement | null {
  let p = t.parentElement;
  while (p && p !== document.body) {
    if (
      p.scrollHeight > p.clientHeight + 1 &&
      /auto|scroll/.test(getComputedStyle(p).overflowY)
    )
      return p;
    p = p.parentElement;
  }
  return null;
}

/** Верх прилипшей к низу панели (не содержит поле, не `.mob-top`), иначе `bot`. */
function barTop(t: Element, bot0: number): number {
  let bot = bot0;
  if (typeof document.elementsFromPoint !== 'function') return bot;
  for (const x of [16, window.innerWidth / 2, window.innerWidth - 16]) {
    (document.elementsFromPoint(x, bot - 6) || []).some((el) => {
      let f: Element | null = el;
      while (
        f &&
        f !== document.body &&
        !/fixed|sticky/.test(getComputedStyle(f).position)
      )
        f = f.parentElement;
      if (!f || f === document.body || f.contains(t) || f.closest('.mob-top'))
        return false;
      bot = Math.min(bot, f.getBoundingClientRect().top);
      return true;
    });
  }
  return bot;
}

function bringIntoView(t: Element): void {
  const a = t.getBoundingClientRect();
  const p = scrollBox(t);
  if (p) {
    const b = p.getBoundingClientRect();
    if (a.bottom > b.bottom - GAP_BOX)
      p.scrollTop += a.bottom - b.bottom + GAP_BOX;
    else if (a.top < b.top + GAP_BOX) p.scrollTop -= b.top + GAP_BOX - a.top;
    return;
  }
  const vv = window.visualViewport;
  const bot = barTop(t, vv ? vv.offsetTop + vv.height : window.innerHeight);
  if (a.bottom > bot - GAP_PAGE)
    window.scrollBy({ top: a.bottom - bot + GAP_PAGE, behavior: 'smooth' });
}

/* ---- верхний слой ---- */
function liftTop(el: HTMLElement): void {
  if (!isPhone() || el.hasAttribute('popover') || el.closest('dialog[open]'))
    return;
  if (el.parentElement && el.parentElement.classList.contains('ds')) return;
  if (getComputedStyle(el).position !== 'fixed') return;
  try {
    el.setAttribute('popover', 'manual');
    el.classList.add('w2a-top');
    el.showPopover();
  } catch {
    el.removeAttribute('popover');
    el.classList.remove('w2a-top');
  }
}

export function PhoneRules({
  everyRoot = false,
}: { everyRoot?: boolean } = {}): React.ReactElement {
  const mark = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const mode = layerMode(mark.current, everyRoot, true);
    if (!mode) return undefined;
    const mine = (n: Element) => layerRoot(n, mode) !== null;
    const on: Document | HTMLElement =
      mode === EVERY_ROOT ? document : mode.root;
    let gone: ReturnType<typeof setTimeout> | undefined;
    let pull: ReturnType<typeof setTimeout> | undefined;
    const out = (e: FocusEvent) => {
      if (!(e.target instanceof Element) || !mine(e.target)) return;
      clearTimeout(gone);
      gone = setTimeout(keyboardGone, 0);
    };
    const into = (e: FocusEvent) => {
      const t = e.target;
      if (
        !(t instanceof Element) ||
        !mine(t) ||
        !isPhone() ||
        !t.matches(TYPING)
      )
        return;
      clearTimeout(pull);
      pull = setTimeout(() => bringIntoView(t), SCROLL_MS);
    };
    let mo: MutationObserver | undefined;
    if (
      typeof HTMLElement.prototype.showPopover === 'function' &&
      typeof MutationObserver !== 'undefined'
    ) {
      const take = (n: Element) => {
        if (n instanceof HTMLElement && mine(n)) liftTop(n);
      };
      mo = new MutationObserver((ms) => {
        for (const m of ms)
          m.addedNodes.forEach((n) => {
            if (!(n instanceof Element)) return;
            if (n.matches(TOP_SEL)) take(n);
            n.querySelectorAll(TOP_SEL).forEach(take);
          });
      });
      mo.observe(layerBase(mode), { childList: true, subtree: true });
    }
    startViewport();
    on.addEventListener('focusout', out as EventListener);
    on.addEventListener('focusin', into as EventListener);
    return () => {
      mo?.disconnect();
      clearTimeout(gone);
      clearTimeout(pull);
      on.removeEventListener('focusout', out as EventListener);
      on.removeEventListener('focusin', into as EventListener);
      stopViewport();
    };
  }, [everyRoot]);
  return <span ref={mark} hidden />;
}
